/* eslint-disable @typescript-eslint/naming-convention */
import { ViewportScroller } from '@angular/common';
import { ApplicationRef, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivationEnd, NavigationCancel, NavigationEnd, NavigationError, Router, Scroll } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { FeedbackService, GraphQLModule, PlusAuthenticationService, dayjs } from '@karve.it/core';

import { Store } from '@ngrx/store';
import { Message, MessageService, PrimeNGConfig } from 'primeng/api';
import { distinctUntilChanged, filter, first } from 'rxjs/operators';
import { SubSink } from 'subsink';


import { MenuService } from './base/menu/app.menu.service';
import { UnsavedChangesToastInfo } from './job/job-create/job-interfaces';
import { OnlineStatusService } from './online-status.service';
import { QuickJsService } from './quickjs/quickjs.service';
import { AppLoadingService } from './services/app-loading.service';
import { AvailableZonesService } from './services/available-zones.service';
import { BrandingService } from './services/branding.service';

import { DatadogService } from './services/datadog.service';
import { DetailsHelperService } from './services/details-helper.service';
import { FreyaHelperService } from './services/freya-helper.service';
import { FreyaNotificationsService } from './services/freya-notifications.service';
import { PageTitleService } from './services/page-title.service';
import { TawkToService } from './services/tawk-to.service';
import { TimezoneHelperService } from './services/timezone-helper.service';
import { UpdateService } from './update.service';

declare global {
  // eslint-disable-next-line no-var
  var calculator: QuickJsService;
}

export interface ValidationItem {
  touched?: boolean;
  valid?: boolean;
  message?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [MessageService]
})
export class AppComponent implements OnInit, OnDestroy {
  menuMode = 'sidebar';

  inputStyle = 'filled';

  ripple: boolean;

  mutateContainerEnabled = true;

  defaultToastlifetime = 3500;

  readyToTryAgain = true;

  subs = new SubSink();

  lastConnectionStatus: string;

  // PWA/Service Workers
  isOnline: boolean;

  unsavedChangesReminders: UnsavedChangesToastInfo[] = [];

  contactAccountingButtonText = 'Contact Accounting';

  private authCheckInterval;

  constructor(
    public store: Store,
    public onlineStatusService: OnlineStatusService,
    public plusAuth: PlusAuthenticationService,
    public appLoadingService: AppLoadingService,
    private primengConfig: PrimeNGConfig,
    private notifications: FreyaNotificationsService,
    private messages: MessageService,
    private detailsHelper: DetailsHelperService,
    private freyaHelper: FreyaHelperService,
    private menuService: MenuService,
    private brandingService: BrandingService,
    private pageTitleService: PageTitleService,
    private graphqlModule: GraphQLModule,
    private router: Router,
    private availableZones: AvailableZonesService,
    private datadogSvc: DatadogService,
    private tawkToSvc: TawkToService,
    private appRef: ApplicationRef,
    private timeZoneHelper: TimezoneHelperService,
    private feedbackService: FeedbackService,
    private viewportScroller: ViewportScroller,
    private cd: ChangeDetectorRef,
    public updateService: UpdateService,
    private swUpdate: SwUpdate,
    private quickJs: QuickJsService,
  ) {
    this.isOnline = false;

    router.events.pipe(filter((event): event is Scroll => event instanceof Scroll)).subscribe((_event) => {

    });
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilityChanged() {
    this.freyaHelper.visibilityChanged.next(document.visibilityState === 'visible');
  }

  async ngOnInit() {
    // If the URL is a navigator URL and the user is not authenticated, redirect to login.
    // Belt-and-suspenders: SetUrlZoneGuard can hang navigation for this route hierarchy.
    const checkNavigatorAuth = () => {
      if (!this.plusAuth.user && window.location.pathname.includes('/navigator/')) {
        console.log('[AppMain] navigator URL without auth, redirecting to login');
        this.plusAuth.goToLogin({}, window.location.pathname + window.location.search);
      }
    };
    checkNavigatorAuth();
    this.subs.sink = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
    ).subscribe(() => checkNavigatorAuth());

    this.checkIfOnOldSite();
    /*this.subs.sink = this.unsavedChangesReminders$.subscribe((unsavedChangesReminders) => {
      if (unsavedChangesReminders?.length) {

        this.unsavedChangesReminders = cloneDeep(unsavedChangesReminders);

        unsavedChangesReminders.forEach((reminder) => {
          const jobCode = reminder.jobCode;
          const jobCustomer = reminder.jobCustomer;

          this.notifications.permanentReminder(
            `${jobCode} for ${jobCustomer} has unsaved changes | Visit the job to save your changes`
          );
        });
      }
    });*/

    this.onlineStatusService.isOnline$.subscribe(status => {
      this.isOnline = status;
    });

    // Initialization sequence — order matters:
    // 1. Datadog must init first so session recording captures the full
    //    lifecycle, including the auth flow. It's synchronous and fast.
    // 2. watchForUpdates() registers the SW update listener and checks for
    //    a new version. If a new version is ready, it forces a reload
    //    before auth runs — critical for the auth cutover so the old shell
    //    doesn't try the legacy login flow.
    // 3. startAuthChecker() is awaited — nothing below runs until auth
    //    completes. This ensures downstream services (tawk.to, branding,
    //    menus, etc.) have an authenticated user available.
    // 4. tawk.to init runs after auth so it can attach user identity to
    //    the chat widget.
    this.datadogSvc.init();
    this.updateService.watchForUpdates();
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.checkForUpdate();
      } catch { /* non-fatal */ }
    }
    await this.startAuthChecker();
    this.tawkToSvc.init();

    this.setDayjsLocale();
    this.timeZoneHelper.watchTimezone();
    this.primengConfig.ripple = true;

    this.subs.sink = this.notifications.addToast.subscribe((input) => {
      if (!input.life) { input.life = this.defaultToastlifetime; }
      this.messages.add(input);
    });

    this.subs.sink = this.notifications.clearToasts.subscribe(() => {
      this.messages.clear();
    });

    this.subs.sink = this.brandingService.currentZone().subscribe((currentZone) => {
      this.datadogSvc.identify();

      if (currentZone) {
        this.menuService.setSidebar();
      }
      // Clear the details pane to prevent showing information that is no longer in context
      this.detailsHelper.detailsItem.next(null);
      this.pageTitleService.init();

      if (currentZone && this.appLoadingService.appLoading.value
        && !this.plusAuth.user
      ) {
        this.appLoadingService.appLoading.next(false);
      }
    });

    this.subs.sink = this.brandingService
      .loading
      .pipe(
        distinctUntilChanged(),
      ).subscribe((loading) => {
        this.appLoadingService.appLoading.next(loading);
    });

    // Notify if we are having connection issues
    this.subscribeToReconnectedEvent();
    this.subscribeToDisconnectedEvent();

    this.initZoneInQueryParam();

    this.subs.sink = this.plusAuth.authState
      .pipe(filter(state => state !== 'deauthenticated' && state !== 'deauthenticating'))
      .subscribe((_state) => {
        this.brandingService.reloadCurrentZone();
        this.brandingService.reloadBranding();
        this.availableZones.reload();
    });

    this.subs.sink = this.plusAuth.authState
      .pipe(filter(state => state === 'deauthenticated'))
      .subscribe(() => {
        this.appLoadingService.appLoading.next(false);
      });


    this.brandingService.watchCurrentZone();

    this.freyaHelper.manageSubscriptions();

    this.freyaHelper.lockDateSupportInfo$.subscribe((info) => {
      this.contactAccountingButtonText = info?.contactAccountingButtonText || 'Contact Accounting';
    });

    window.calculator = this.quickJs;
  }

  checkIfOnOldSite() {
    const url = new URL(location.href);
    const oldSites = [ 'admin.moveright.app', 'app.youmoveme.com' ];
    const newSite = 'moveright.app';

    if (oldSites.includes(url.hostname)) {
      url.hostname = newSite;

      // redirect to new site
      window.location.href = url.href;
    }
  }

  subscribeToReconnectedEvent() {

    this.lastConnectionStatus = this.plusAuth.connectionStatus.value;
    this.subs.sink = this.plusAuth.connectionStatus.subscribe((status) => {
      const reconnected = status === 'connected' && this.lastConnectionStatus === 'disconnected';

       if (reconnected) {
        this.messages.clear();
        this.messages.add({
          severity: 'success',
          summary: 'Reconnected',
          detail: `Network Status: ${ status }`,
          sticky: false,
          life: 3000,
        });
        console.error(`Reconnected`, status);
      }

      if (reconnected && this.appLoadingService.appLoading.value) {
        location.reload();
      }

      this.lastConnectionStatus = this.plusAuth.connectionStatus.value;
    });
  }

  subscribeToDisconnectedEvent() {
    this.subs.sink = this.plusAuth.connectionStatus.pipe(
      distinctUntilChanged(),
    ).subscribe((status) => {
      if (status === 'disconnected') {
        this.messages.clear();
        this.messages.add({
          severity: 'error',
          summary: 'No connection',
          detail: `Network Status: ${ status }`,
          sticky: false,
          life: this.plusAuth.pollingInterval - 500,
        });
        console.error(`No Connection`, status);
      }
    });
  }

  /**
   * Initialize subscriptions to set the zone
   * in the URL query parameter for all pages.
   * Zone will be added to the page search params
   */
  async initZoneInQueryParam() {
    // The angular query params are not yet populated so
    // we must set them here.
    const url = new URL(location as any);
    const queryParamZone = url.searchParams.get('zone');
    const ssZone = sessionStorage.getItem('zone');
    const ssRole = sessionStorage.getItem('role');
    const lsZone = localStorage.getItem('zone');
    const lsRole = localStorage.getItem('role');
    const zone = queryParamZone || ssZone || lsZone;
    const role = ssRole || lsRole;
    this.graphqlModule.zone.next(zone);
    this.graphqlModule.role.next(role);
    // console.log(url, queryParamZone, zone, role);

    this.subs.sink = this.router.events.subscribe((ev) => {
      if (ev instanceof ActivationEnd) {
        this.plusAuth.updateZoneInNavigation();
      }
    });

    this.subs.sink = this.graphqlModule.zone.subscribe((_res) => {
      // set last zone set in session storage + local storage
      // so new tabs will be correctly resolved.
      // wait for end of the event loop for role to also update
      setTimeout(() => {
        this.setStoredContext();
      }, 1);
    });
  }

  setStoredContext() {
    const gqlZone = this.graphqlModule.zone.value;
    const gqlRole = this.graphqlModule.role.value;
    // console.log(`Stored context updated`, gqlZone, gqlRole);
    if (gqlZone) {
      sessionStorage.setItem('zone', gqlZone);
      localStorage.setItem('zone', gqlZone);
    }

    if (gqlRole) {
      sessionStorage.setItem('role', gqlRole);
      localStorage.setItem('role', gqlRole);
    }
  }

  setDayjsLocale() {
    dayjs.updateLocale('en', {
      relativeTime : {
        future: "in %s",
        past: "%s ago",
        s: 'a few seconds',
        m: "a minute",
        mm: "%d minutes",
        h: "an hour",
        hh: "%d hours",
        d: "a day",
        dd: "%d days",
        M: "a month",
        MM: "%d months",
        y: "a year",
        yy: "%d years"
      }
  });
  }

  /**
   * Initializes an interval to check authentication every 5 seconds
   * and immediately performs an auth check if the user is currently
   * authenticated.
   *
   * An auth check will check the JWT. If it is expired/invalid then
   * it will try to use the current refresh token to request a new JWT.
   * If the refresh token is invalid the user will be de-authenticated.
   */
  async startAuthChecker() {
    this.subs.sink = this.appRef.isStable
      .pipe(first((stable) => stable))
      .subscribe(() => {
        this.authCheckInterval = setInterval(() => {
          this.plusAuth.checkAuthStatus().catch((err) => {
            console.error(`Could not check auth status`, err);
          });
        }, 5000);
      });

    if (this.plusAuth.user) {
      await this.plusAuth.checkAuthStatus();

      if (this.plusAuth.user) {
        console.log(`User validated:`, this.plusAuth.user.id);
      } else {
        console.log(`Session invalidated`);
      }
    }

    // Marketing content now lives on the same domain (moveright.app) — served
    // by a Cloudflare Worker that routes marketing paths to the Astro static
    // site.  The Angular app only handles authenticated routes (/dashboard,
    // /jobs, /schedule, etc.) plus /auth/*.  If an unauthenticated user
    // somehow reaches an Angular route, UserAuthGuard redirects to /auth/login.
    //
    // No cross-domain redirect is needed anymore.
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    clearInterval(this.authCheckInterval);
  }

  refreshMutateContainer(){
    this.mutateContainerEnabled = false;
    setTimeout(() => {
      this.mutateContainerEnabled = true;
    }, 2500);
  }

  reportBug(input: Message) {

    const maxSubjectLength = 100;

    const subject = input.summary.substring(0, maxSubjectLength);

    const message = input.summary + ': ' + input.detail;

    this.feedbackService.openFeedbackDialog({
      subject,
      message,
      feedbackType: 'bug',
    });
  }

  contactAccounting(message: Message) {

    const { eventId } = message?.data;

    if (!eventId) {
      console.error('No eventId found in message data');
      return;
    }

    this.freyaHelper.contactAccounting(eventId);
  }

  getSupportType(message: Message) {
    if (!this.freyaHelper.lockDateSupportInfo$.value) {
      return 'support';
    }
    if (message?.data?.code === 'LOCK_DATE_ERROR') {
      return 'accounting';
    }
    return 'support';
  }
}
